<script setup lang="ts">
import { computed } from "vue";
import { fr } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import { timezoneToParsedOffset, minTwoDigits } from "@/utils";

import { EffectiveFlight } from '@/interfaces';

import {
  ArrowUpRight,
  ArrowDownRight,
  SquareArrowDownRight,
  SquareArrowUpRight,
} from "lucide-vue-next"

const props = defineProps<{
  claimTypeName: any;
  scheduledDepartureUTC: Date;
  scheduledArrivalUTC: Date;
  departureAirport: EffectiveFlight["departureAirport"];
  arrivalAirport: EffectiveFlight["arrivalAirport"];
  utc: boolean;
}>();

function formatFlightDate(date: Date, timezone: string, format?: string = 'dd/MM/yyyy') {
  const tz = props.utc ? "UTC" : timezone;
  return formatInTimeZone(date, tz, format, { locale: fr })
}

const timezoneOffsetDepartureAirport = computed(
  () => timezoneToParsedOffset(props.departureAirport.timezone, props.scheduledDepartureUTC)
);

const timezoneOffsetArrivalAirport = computed(
  () => timezoneToParsedOffset(props.arrivalAirport.timezone, props.scheduledArrivalUTC)
);
</script>
<template>
  <div>
    <div class="flex flex-row items-center h-10 bg-gray-100 border-b">
      <div class="inline-flex items-center ml-4 text-base font-bold text-gray-500 uppercase flex-2">
        <SquareArrowUpRight v-if="true" class="mr-2 text-zinc-500 fill-white" />
        <ArrowUpRight v-else class="w-6 h-6 mr-2 text-slate-600" />
        <svg v-if="false" version="1.0" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 mr-2 fill-gray-500" viewBox="0 0 512.000000 512.000000"
          preserveAspectRatio="xMidYMid meet">
          <g transform="translate(0.000000,512.000000) scale(0.100000,-0.100000)" stroke="none">
            <path d="M1350 4706 c-107 -30 -214 -59 -236 -65 l-42 -11 515 -893 c284 -490
  517 -899 520 -907 3 -12 -101 -43 -519 -154 -288 -77 -569 -151 -624 -166
  l-101 -28 -249 195 -248 195 -178 -47 c-98 -27 -178 -53 -178 -58 0 -6 145
  -261 321 -567 l322 -557 121 32 c3476 931 4095 1099 4149 1128 137 72 216 231
  190 383 -33 191 -185 320 -373 318 -64 -1 -174 -27 -753 -183 -372 -101 -681
  -181 -686 -179 -5 2 -397 365 -872 808 -474 443 -867 806 -873 807 -6 1 -99
  -22 -206 -51z"/>
  <path d="M170 610 l0 -250 2395 0 2395 0 0 250 0 250 -2395 0 -2395 0 0 -250z"/>
          </g>
        </svg>
        Départ
        <span class="ml-4 text-xs font-medium text-gray-900 leading-[1.15rem] ">{{
          formatFlightDate(scheduledDepartureUTC, departureAirport.timezone, "d MMMM yyyy")
        }}</span>
      </div>
      <div class="inline-flex items-center ml-4 text-base font-bold text-gray-500 uppercase flex-2">
        <SquareArrowDownRight v-if="true" class="mr-2 text-zinc-500" />
        <ArrowDownRight v-else class="w-6 h-6 mr-2 text-slate-600" />
        <svg v-if="false" version="1.0" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 mr-2 fill-gray-500"
          viewBox="0 0 512.000000 512.000000" preserveAspectRatio="xMidYMid meet">
          <g transform="translate(0.000000,512.000000) scale(0.100000,-0.100000)" stroke="none">
            <path d="M2050 4005 l0 -1115 -22 5 c-13 3 -313 84 -668 179 l-645 173 -124
  309 c-68 170 -126 311 -127 313 -5 6 -357 101 -376 101 -17 0 -18 -40 -18
  -693 l0 -693 2243 -602 c1233 -331 2267 -604 2298 -608 246 -29 469 212 429
  462 -20 125 -112 250 -222 302 -24 11 -372 108 -772 215 -400 108 -729 197
  -730 199 -2 2 -168 545 -370 1208 -202 663 -372 1209 -376 1214 -7 6 -499 145
  -517 146 -2 0 -3 -502 -3 -1115z" />
            <path d="M0 270 l0 -270 2560 0 2560 0 0 270 0 270 -2560 0 -2560 0 0 -270z" />
          </g>
        </svg>
        Arrivée
        <span class="ml-4 text-xs font-medium ">{{}}</span>
        <!-- <span
              class="bg-gray-200 text-gray-600 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-4">
              ➜ Il y a {{ formatDistanceToNow(new Date(flight.scheduledArrivalUTC), { locale: fr }) }}
            </span> -->
      </div>
    </div>
    <div class="flex flex-row border-b-1"
      :class="claimTypeName === 'FLIGHT_CANCELLATION' || claimTypeName === 'PNR_SEGMENT_CANCELLATION' || claimTypeName === 'OVERBOOKING' ? 'border-l-2 border-red-600' : claimTypeName ? 'border-l-2 border-amber-500' : 'border-l-2 border-blue-500'">
      <div class="flex-2">
        <div class="flex flex-col items-start my-3 ml-4">
          <p class="mb-1 text-xl font-bold text-left">
            {{ departureAirport.city }}
            <span class="text-base font-normal">(<span class="font-bold">{{ departureAirport.iata }}</span>)</span>
          </p>
          <p class="text-sm text-left text-gray-700">{{ departureAirport.name }}
          </p>
          <p class="mt-2 text-xs">
            {{ departureAirport.timezone }}
            (GMT {{timezoneOffsetDepartureAirport.sign}}{{ minTwoDigits(timezoneOffsetDepartureAirport.hours) }}:{{
            minTwoDigits(timezoneOffsetDepartureAirport.minutes) }})
          </p>
        </div>
      </div>

      <!-- <img class="absolute mt-[7.35rem] ml-52" src="/airplane.svg" alt="destination" width="50" /> -->

      <div class="flex-2">
        <div class="flex flex-col items-start my-3 ml-4">
          <p class="mb-1 text-xl font-bold text-left">
            {{ arrivalAirport.city }}
            <span class="text-base font-normal">(<span class="font-bold">{{ arrivalAirport.iata }}</span>)</span>
          </p>
          <p class="text-sm text-left text-gray-700">{{ arrivalAirport.name }}
          </p>
          <p class="mt-2 text-xs">
            {{ arrivalAirport.timezone }}
            (GMT {{timezoneOffsetArrivalAirport.sign}}{{ minTwoDigits(timezoneOffsetArrivalAirport.hours) }}:{{
            minTwoDigits(timezoneOffsetArrivalAirport.minutes) }})
          </p>
        </div>
      </div>
    </div>
  </div>
</template>